// Öffentliche Gassi-Preisleiter (15.09.2026, Founder-Entscheid: Preise transparent).
//
// Einzige Quelle: PASS_PRICES / PASS_PRODUCTS aus src/lib/pricing.ts (Schaufenster-Tarif
// für Neukunden, Spiegel von supabase/functions/_shared/pass-catalog.ts). Nie Zahlen hier
// oder in Seiten hart eintragen — die DB-Tabelle `pricing` ist Altbestand und falsch.
// Relativer Import (12.10.2026): vite.config.ts liest diese Datei beim Build in Node
// (llms.txt, Roh-HTML), dort gibt es den `@/`-Alias nicht. pricing.ts ist frei von Imports.
import { PASS_PRODUCTS, type PassType } from "./pricing.ts";

export type GassiKarteId = Extract<PassType, "three" | "five_regular" | "ten_regular" | "single">;

export interface GassiKarte {
  id: GassiKarteId;
  name: string;
  /** Endpreis in Euro inkl. MwSt. */
  price: number;
  credits: number;
  /** Preis pro Ausflug in Euro (nicht gerundet). */
  perAusflug: number;
}

const karte = (id: GassiKarteId, name: string): GassiKarte => {
  const p = PASS_PRODUCTS.find((x) => x.id === id);
  if (!p) throw new Error(`Pass ${id} fehlt in PASS_PRODUCTS`);
  return { id, name, price: p.price, credits: p.credits, perAusflug: p.price / p.credits };
};

/** Reihenfolge = Kundenreise: Einstieg zuerst, dann die Karten für regelmäßige Walks. */
export const GASSI_KARTEN: GassiKarte[] = [
  karte("three", "Einstiegs-Karte"),
  karte("five_regular", "5er-Karte"),
  karte("ten_regular", "10er-Karte"),
  karte("single", "Tageskarte"),
];

export const gassiKarte = (id: GassiKarteId): GassiKarte =>
  GASSI_KARTEN.find((k) => k.id === id) as GassiKarte;

/** Gutschrift aus der Einstiegs-Karte für die nächste 5er-/10er-Karte (binnen 60 Tagen).
 *  Technisch eine Gutschrift, die im Checkout als Coupon angerechnet wird (pass-credits.ts).
 *  28.09.2026: nach außen als Gutschrift statt Rabatt, und ohne „effektiv 26,33 €“ —
 *  der Effektivpreis setzte einen Anker, gegen den 44 € wie ein Aufschlag wirkten.
 *  Nicht „Guthaben“ nennen: das heißt im Kundenbereich die Zahl der übrigen Ausflüge. */
export const EINSTIEG_GUTSCHRIFT_EUR = 60;
export const EINSTIEG_GUTSCHRIFT_TAGE = 60;

/** Ersparnis einer Karte gegenüber gleich vielen Tageskarten, in Euro. */
export function ersparnisGegenTageskarte(k: GassiKarte): number {
  const tag = GASSI_KARTEN.find((x) => x.id === "single")!;
  return tag.price * k.credits - k.price;
}

/** Günstigster Listenpreis pro Ausflug (aktuell 10er-Karte). */
export const GASSI_AB_PRO_AUSFLUG = Math.min(...GASSI_KARTEN.map((k) => k.perAusflug));

/** 44 → „44 €", 46.333 → „46,33 €". */
export function euro(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return (
    rounded.toLocaleString("de-DE", {
      minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}

/** Preis pro Ausflug OHNE Währungszeichen, für Karten, die den Ausflugspreis groß
 *  zeigen. Der Gesamtpreis muss daneben stehen bleiben — er ist der Betrag, den der
 *  Kunde zahlt. */
export function proAusflug(k: GassiKarte): string {
  return euro(k.perAusflug).replace(" €", "");
}
